import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, ArrowRight, MapPin } from "lucide-react";

const EVENTS = ["wedding", "corporate", "conference", "leisure", "family"];

// Popular localities per city. Selecting an area narrows the bid pool to hotels
// whose address contains this locality — with an automatic fallback to city-wide
// notifications if no area-specific hotel is registered yet.
const AREAS_BY_CITY = {
  "Bangalore": ["Koramangala", "Indiranagar", "Whitefield", "MG Road", "Electronic City", "Marathahalli", "HSR Layout", "Jayanagar"],
  "Bengaluru": ["Koramangala", "Indiranagar", "Whitefield", "MG Road", "Electronic City", "Marathahalli", "HSR Layout", "Jayanagar"],
  "Mumbai": ["Bandra", "Andheri", "Colaba", "Powai", "Juhu", "Lower Parel", "BKC", "Worli"],
  "Delhi": ["Connaught Place", "South Delhi", "Karol Bagh", "Aerocity", "Paharganj", "Saket", "Nehru Place"],
  "New Delhi": ["Connaught Place", "South Delhi", "Karol Bagh", "Aerocity", "Paharganj", "Saket", "Nehru Place"],
  "Hyderabad": ["Banjara Hills", "Jubilee Hills", "Gachibowli", "HiTech City", "Madhapur", "Kondapur", "Secunderabad"],
  "Chennai": ["T Nagar", "Nungambakkam", "Anna Nagar", "OMR", "ECR", "Egmore", "Adyar", "Marina"],
  "Kolkata": ["Park Street", "Salt Lake", "New Town", "Ballygunge", "Alipore", "Howrah"],
  "Pune": ["Koregaon Park", "Kalyani Nagar", "Hinjewadi", "Viman Nagar", "Baner", "Kharadi", "Aundh"],
  "Ahmedabad": ["SG Highway", "Ellisbridge", "Navrangpura", "Bopal", "Prahlad Nagar", "CG Road"],
  "Jaipur": ["MI Road", "C Scheme", "Malviya Nagar", "Vaishali Nagar", "Amer", "Mansarovar"],
  "Goa": ["Baga", "Calangute", "Anjuna", "Candolim", "Panjim", "Palolem", "Vagator"],
  "Kochi": ["Fort Kochi", "Ernakulam", "MG Road", "Marine Drive", "Kakkanad"],
  "Lucknow": ["Hazratganj", "Gomti Nagar", "Aliganj", "Indira Nagar", "Charbagh"],
  "Chandigarh": ["Sector 17", "Sector 22", "Sector 35", "IT Park", "Manimajra"],
};

export default function BookingRequests() {
  const [list, setList] = useState([]);
  const [availableCities, setAvailableCities] = useState([]);
  const [form, setForm] = useState({ city: "", area: "", checkin: "", checkout: "", rooms_needed: 1, event_type: "wedding", budget_per_room: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);

  const load = () => api.get("/booking-requests", { params: { mine: true } })
    .then(({ data }) => setList(Array.isArray(data) ? data : []))
    .catch(() => setList([]));
  useEffect(() => { load(); }, []);

  // Pull all cities where we have hotels registered so the dropdown reflects
  // real supply. Fall back to the manual AREAS_BY_CITY keys if backend is empty.
  useEffect(() => {
    api.get("/hotels/cities").then(({ data }) => {
      const cities = Array.isArray(data) ? data : [];
      setAvailableCities(cities.length ? cities : Object.keys(AREAS_BY_CITY));
    }).catch(() => setAvailableCities(Object.keys(AREAS_BY_CITY)));
  }, []);

  const areasForCity = useMemo(() => AREAS_BY_CITY[form.city] || [], [form.city]);

  // Restrict date pickers so users cannot pick past dates. Today's ISO date
  // (YYYY-MM-DD) is the minimum for check-in; check-in itself is the minimum
  // for check-out to enforce checkout > checkin.
  const todayIso = new Date().toISOString().slice(0, 10);
  const minCheckout = form.checkin || todayIso;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.city || !form.checkin || !form.checkout) return toast.error("Fill required fields");
    if (form.checkin < todayIso) return toast.error("Check-in date cannot be in the past");
    if (form.checkout <= form.checkin) return toast.error("Check-out must be after check-in");
    setSubmitting(true);
    try {
      const { data } = await api.post("/booking-requests", form);
      toast.success(`Request posted! ${data.notified_partners || 0} hotel${(data.notified_partners || 0) === 1 ? "" : "s"} notified.`);
      setForm({ city: "", area: "", checkin: "", checkout: "", rooms_needed: 1, event_type: "wedding", budget_per_room: "", notes: "" });
      load();
    } catch { toast.error("Failed"); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="bg-slate-50 min-h-[calc(100vh-4rem)]">
      <div className="max-w-6xl mx-auto px-4 md:px-8 py-10 grid lg:grid-cols-12 gap-8">
        <div className="lg:col-span-7">
          <div className="ss-label text-[#A6852F]">Group Booking Bids</div>
          <h1 className="font-head text-4xl md:text-5xl font-black text-slate-900 mt-3">Your group bookings</h1>
          <p className="text-slate-600 mt-2 max-w-lg leading-relaxed">Post once. <span className="text-[#A6852F] font-semibold">AI-negotiated bids</span> from matching hotels arrive within minutes — each with hotel-specific reasoning on why the price is fair. Pick the best.</p>

          {list.length === 0 ? (
            <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-10 text-center">
              <p className="text-slate-600">No group bookings yet — post your first one →</p>
            </div>
          ) : (
            <div className="space-y-3 mt-6" data-testid="my-requests-list">
              {list.map((r) => (
                <Link
                  to={`/requests/${r.request_id}`}
                  key={r.request_id}
                  className="rounded-2xl border border-slate-200 bg-white p-5 flex items-center justify-between ss-hover-glow transition-all block"
                  data-testid={`request-${r.request_id}`}
                >
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-head font-bold text-lg text-slate-900">{r.city}{r.area ? ` · ${r.area}` : ""}</h3>
                      <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-[#C8A34D]/12 border border-[#C8A34D]/30 text-[#A6852F] text-[10px] font-bold uppercase tracking-wide">{r.event_type}</span>
                    </div>
                    <div className="text-sm text-slate-500 mt-0.5">{r.checkin} → {r.checkout} · {r.rooms_needed} rooms · budget ₹{r.budget_per_room || "—"}</div>
                  </div>
                  <ArrowRight className="w-5 h-5 text-[#A6852F]" />
                </Link>
              ))}
            </div>
          )}
        </div>

        <div className="lg:col-span-5">
          <form onSubmit={submit} className="rounded-2xl border border-slate-200 bg-white p-6 sticky top-20 shadow-[0_20px_60px_-24px_rgba(200,163,77,0.35)]">
            <div className="ss-label text-[#A6852F]">New group booking</div>
            <h3 className="font-head text-2xl font-black mt-2 text-slate-900">Group Booking Bid</h3>
            <p className="text-xs text-slate-500 mt-1">AI-enabled hotels in your city will auto-bid based on live occupancy, weekend demand, and your preferences.</p>
            <div className="space-y-3 mt-5">
              <div>
                <Label className="hw-label">City *</Label>
                <Select value={form.city || undefined} onValueChange={(v) => setForm({...form, city: v, area: ""})}>
                  <SelectTrigger data-testid="req-city-select" className="rounded-sm mt-1"><SelectValue placeholder="Pick a city" /></SelectTrigger>
                  <SelectContent>
                    {availableCities.filter(Boolean).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {form.city && (
                <div>
                  <Label className="hw-label flex items-center gap-1"><MapPin className="w-3 h-3" /> Area / Locality (optional)</Label>
                  <p className="text-[11px] text-slate-500 mt-0.5">Pick an area to prioritize nearby hotels. Others will still see it if none are in that area.</p>
                  {areasForCity.length > 0 ? (
                    <div className="flex flex-wrap gap-1.5 mt-2" data-testid="req-area-chips">
                      {areasForCity.map((a) => (
                        <button
                          key={a} type="button"
                          onClick={() => setForm({...form, area: form.area === a ? "" : a})}
                          className={`px-2.5 py-1 border rounded-sm text-xs font-bold transition-colors ${form.area === a ? "border-blue-700 bg-blue-50 text-blue-700" : "border-slate-300 text-slate-700 hover:border-slate-500"}`}
                          data-testid={`req-area-${a.replace(/\s+/g,'-').toLowerCase()}`}
                        >{a}</button>
                      ))}
                    </div>
                  ) : (
                    <Input data-testid="req-area-input" value={form.area} onChange={(e) => setForm({...form, area: e.target.value})} className="rounded-sm mt-2" placeholder="e.g. downtown, near airport" />
                  )}
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="hw-label">Check-in *</Label><Input data-testid="req-checkin-input" type="date" min={todayIso} value={form.checkin} onChange={(e) => setForm({...form, checkin: e.target.value})} className="rounded-sm mt-1" /></div>
                <div><Label className="hw-label">Check-out *</Label><Input data-testid="req-checkout-input" type="date" min={minCheckout} value={form.checkout} onChange={(e) => setForm({...form, checkout: e.target.value})} className="rounded-sm mt-1" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="hw-label">Rooms</Label><Input data-testid="req-rooms-input" type="number" min={1} value={form.rooms_needed} onChange={(e) => setForm({...form, rooms_needed: e.target.value})} className="rounded-sm mt-1" /></div>
                <div>
                  <Label className="hw-label">Event Type</Label>
                  <Select value={form.event_type} onValueChange={(v) => setForm({...form, event_type: v})}>
                    <SelectTrigger data-testid="req-event-select" className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>{EVENTS.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label className="hw-label">Budget per room (₹)</Label><Input data-testid="req-budget-input" type="number" value={form.budget_per_room} onChange={(e) => setForm({...form, budget_per_room: e.target.value})} className="rounded-sm mt-1" placeholder="2500" /></div>
              <div><Label className="hw-label">Notes</Label><Textarea data-testid="req-notes-input" value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} className="rounded-sm mt-1" placeholder="Vegetarian catering, etc." /></div>
              <Button type="submit" disabled={submitting} className="w-full ss-btn ss-btn-primary py-5 h-auto" data-testid="req-submit-btn">
                <Plus className="w-4 h-4 mr-2" /> {submitting ? "Posting…" : "Post Group Booking Bid"}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
